export type CanonicalPipeline = {
  id: number;
  pipeline_name: string;
  operator: string;
  display_name: string | null;
  pipeline_code: string | null;
  diameter_source_url: string | null;
  capacity_source_url: string | null;
  maop_source_url: string | null;
  ebb_endpoint: string | null;
  ebb_scraper_status: 'active' | 'inactive' | 'never' | null;
  last_verified_at: string | null;
  notes: string | null;
  days_since_verified: number | null;
  segment_count: number | null;
  total_miles: number | null;
  max_diameter_in: number | null;
  capacity_mmcf_day_max: number | null;
  utilization_pct_avg: number | null;
  maop_psig_max: number | null;
  latest_design_capacity_mmcf: number | null;
  latest_scheduled_mmcf: number | null;
  latest_utilization_pct: number | null;
  latest_gas_day: string | null;
};

function makeHeaders(token?: string | null): Record<string, string> {
  const auth = token ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('pp-auth') : null);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth) headers['Authorization'] = `Bearer ${auth}`;
  return headers;
}

export async function fetchPipelineInventory(token?: string): Promise<CanonicalPipeline[]> {
  const res = await fetch('/api/admin/pipelines', { headers: makeHeaders(token), credentials: 'include' });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = await res.json();
  if (!Array.isArray(data?.pipelines)) {
    throw new Error('Malformed response: expected { pipelines: [...] }');
  }
  return data.pipelines as CanonicalPipeline[];
}

export type RefreshEvidence = {
  gasDayBefore: string | null;
  gasDayAfter: string | null;
  rowsAdded: number;
  rowCountBefore: number;
  rowCountAfter: number;
  sourceUrl: string | null;
};

export async function refreshPipeline(
  id: number,
  token?: string
): Promise<{ pipeline: CanonicalPipeline; evidence: RefreshEvidence; scraperOutput?: string }> {
  const res = await fetch(`/api/admin/pipelines/${id}/refresh`, {
    method: 'POST',
    headers: makeHeaders(token),
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as { error?: string; stderr?: string; sourceUrl?: string });
    throw Object.assign(new Error(err.error ?? `${res.status} ${res.statusText}`), {
      status: res.status,
      stderr: err.stderr,
      sourceUrl: err.sourceUrl,
    });
  }
  return data as { pipeline: CanonicalPipeline; evidence: RefreshEvidence; scraperOutput?: string };
}
